import { FacilityGroupIcon } from './../../services/mergeIconHotelFacilities';
import { mergeBeachLineWithBeach } from './../../services/mergeBeachLineWithBeach';
import { intersectionWith } from 'lodash';
import { observable, computed, action } from 'mobx';
import {
    Facility,
    FacilityGroup,
} from 'sletat-api-services/lib/sletat/models/initial-state/page-models/hotel';
import { mergeIconHotelFacilities } from '../../services/mergeIconHotelFacilities';

import {
    FACILITIES_FOR_KIDS_GROUP_ID,
    HOTEL_COMMON_FACILITIES_GROUP_ID,
    HOTEL_MEALS_FACILITIES_GROUP_ID,
} from '../consts';
import {
    basicFacilitiesGroupsIds,
    facilitiesGroupIdsOrder,
    mappedBasicHotelFacilitiesIds,
} from '../consts/hotel';
import {
    FacilityWithGroupId,
    HotelBasicFacilityGroups,
} from '../models/hotel-info';
import { getHowLongMinutesToCenterCity } from '../utils/initial-state';
import { InitialStateStore } from './initial-state';
import { Room } from '../models/hotel-room-types';

interface HotelDetails {
    countryId: number | null;
    countryName: string | null;
    countryAlias: string | null;
    resortName: string | null;
    resortId: number | null;
    resortAlias: string | null;
    hotelId: number | null;
    hotelImagesCount: number | null;
    longitude: number | null;
    latitude: number | null;
    hotelName: string | null;
    hotelOriginalName: string | null;
    hotelDescription: string | null;
    hotelEmail: string | null;
    hotelPhone: string | null;
    reconstructionYear: number | null;
    numRooms: number | null;
    categoryName: string | null;
    viewsTodayCount: number | null;
    originalPreviousName: string | null;
    tripAdvisorRate: number | null;
    beachType: string | null;
    beachSandType: string | null;
    distanceToAirport: number | null;
    checkInTime: string | null;
    checkOutTime: string | null;
    distanceToBeach: number | null;
    howLongMinutesToCenterCity: number | null;
    isCenterCity: boolean;
    popularityLevel: number;
    videoUrl: string | null;
    rooms: Array<Room>;
    rate: number | null;
}

export class HotelDetailsStore {
    @observable
    private details: HotelDetails | null;

    constructor(private initialStateStore: InitialStateStore) {
        this.initialStateStore = initialStateStore;
        this.details = this.getInitialHotelInfo();
    }

    @computed
    get countryId(): number | null {
        return (this.details && this.details.countryId) || null;
    }

    @computed
    get countryName(): string | null {
        return (this.details && this.details.countryName) || null;
    }

    @computed
    get rooms(): Array<Room> | null {
        return (this.details && this.details.rooms) || null;
    }

    @computed
    get roomsIds(): number[] {
        if (!this.details?.rooms?.length) return [];
        return this.details.rooms.map(room => room.id);
    }

    getRoomBySystemRoomTypeId(systemRoomTypeId: number | null): Room | null {
        return this.rooms?.find(room => room.id === systemRoomTypeId) ?? null;
    }

    @computed
    get facilityGroups(): FacilityGroupIcon[] {
        if (!this.initialStateStore.pageModel) {
            return [];
        }

        let facilitiesGroups = mergeIconHotelFacilities(
            this.initialStateStore.pageModel?.facilitiesGroups
        );
        facilitiesGroups = mergeBeachLineWithBeach(facilitiesGroups);

        return (
            facilitiesGroups?.sort((a, b) => {
                let indexOfA = facilitiesGroupIdsOrder.indexOf(a.id);
                let indexOfB = facilitiesGroupIdsOrder.indexOf(b.id);

                if (indexOfA === -1) {
                    indexOfA = facilitiesGroupIdsOrder.length;
                }
                if (indexOfB === -1) {
                    indexOfB = facilitiesGroupIdsOrder.length;
                }

                return indexOfA - indexOfB;
            }) ?? []
        );
    }

    @computed
    get basicFacilitiesGoups(): FacilityWithGroupId[] {
        const facilitiesGroups =
            this.initialStateStore.pageModel?.facilitiesGroups;

        if (!facilitiesGroups || facilitiesGroups.length === 0) {
            return [];
        }

        const basicFacilities = facilitiesGroups
            .filter((group) => basicFacilitiesGroupsIds.includes(group.id))
            .map((group) => this.getFacilityWithGroupId(group))
            .filter(Boolean) as FacilityWithGroupId[];

        const transportationGroup = basicFacilities.find(
            (f) => f?.groupId === HotelBasicFacilityGroups.Transportation
        );

        if (!transportationGroup && !!this.distanceToAirport) {
            basicFacilities.splice(2, 0, {
                id: -1,
                name: `${this.distanceToAirport} км до аэропорта`,
                groupId: HotelBasicFacilityGroups.Transportation,
            } as FacilityWithGroupId);
        }

        if (this.checkInTime && this.checkOutTime) {
            basicFacilities.push({
                id: -2,
                name: `Время заселения: ${this.checkInTime}`,
                secondName: `Время выезда: ${this.checkOutTime}`,
                groupId: HotelBasicFacilityGroups.CheckInOutIimes,
            } as FacilityWithGroupId);
        }

        return basicFacilities.slice().sort((a, b) => {
            return (
                basicFacilitiesGroupsIds.indexOf(a.groupId) -
                basicFacilitiesGroupsIds.indexOf(b.groupId)
            );
        });
    }

    @computed
    get hotelCommonFacilities(): Facility[] {
        const group = this.facilityGroups.filter(
            (group) => group.id === HOTEL_COMMON_FACILITIES_GROUP_ID
        )[0];
        return group ? group.facilities : [];
    }

    @computed
    get hotelMealsFacilities(): Facility[] {
        const group = this.facilityGroups.filter(
            (group) => group.id === HOTEL_MEALS_FACILITIES_GROUP_ID
        )[0];
        return group ? group.facilities : [];
    }

    @computed
    get facilitiesForKids(): Facility[] {
        const group = this.facilityGroups.filter(
            (group) => group.id === FACILITIES_FOR_KIDS_GROUP_ID
        )[0];
        return group ? group.facilities : [];
    }

    @computed
    get countryAlias(): string | null {
        return (this.details && this.details.countryAlias) || null;
    }

    @computed
    get resortId(): number | null {
        return (this.details && this.details.resortId) || null;
    }

    @computed
    get resortName(): string | null {
        return (this.details && this.details.resortName) || null;
    }

    @computed
    get resortAlias(): string | null {
        return (this.details && this.details.resortAlias) || null;
    }

    @computed
    get hotelId(): number | null {
        return (this.details && this.details.hotelId) || null;
    }

    @computed
    get hotelLongitude(): number | null {
        return (this.details && this.details.longitude) || null;
    }

    @computed
    get hotelLatitude(): number | null {
        return (this.details && this.details.latitude) || null;
    }

    @computed
    get hotelName(): string | null {
        return (
            (this.details && this.details.hotelOriginalName) ||
            (this.details && this.details.hotelName) ||
            null
        );
    }

    @computed
    get hotelDescription(): string | null {
        return (this.details && this.details.hotelDescription) || null;
    }

    @computed
    get hotelEmail(): string | null {
        return (this.details && this.details.hotelEmail) || null;
    }

    @computed
    get hotelPhone(): string | null {
        return (this.details && this.details.hotelPhone) || null;
    }

    @computed
    get hotelImagesCount(): number | null {
        return (this.details && this.details.hotelImagesCount) || null;
    }

    @computed
    get numHotelRooms(): number | null {
        return (this.details && this.details.numRooms) || null;
    }

    @computed
    get hotelReconstructionYear(): number | null {
        return (this.details && this.details.reconstructionYear) || null;
    }

    @computed
    get previousOriginalHotelName(): string | null {
        return (this.details && this.details.originalPreviousName) || null;
    }

    @computed
    get hotelViewsToday(): number | null {
        return (this.details && this.details.viewsTodayCount) || null;
    }

    @computed
    get categoryName(): string | null {
        if (this.details) {
            return this.details.categoryName === 'Без звезд' ?
            null :
            this.details.categoryName;
        }
        return null;
    }

    @computed
    get tripAdvisorRate(): number | null {
        return (this.details && this.details.tripAdvisorRate) || null;
    }

    @computed
    get beachType(): string | null {
        return (this.details && this.details.beachType) || null;
    }

    @computed
    get beachSandType(): string | null {
        return (this.details && this.details.beachSandType) || null;
    }

    @computed
    get distanceToAirport(): number | null {
        return (this.details && this.details.distanceToAirport) || null;
    }

    @computed
    get checkInTime(): string | null {
        return this.details?.checkInTime ?? null;
    }

    @computed
    get checkOutTime(): string | null {
        return this.details?.checkOutTime ?? null;
    }

    @computed
    get distanceToBeach(): number | null {
        return (this.details && this.details.distanceToBeach) || null;
    }

    @computed
    get howLongMinutesToCenterCity(): number | null {
        return (
            (this.details && this.details.howLongMinutesToCenterCity) || null
        );
    }

    @computed
    get isCenterCity(): boolean {
        return this.details ? this.details.isCenterCity : false;
    }

    @computed
    get popularityLevel(): number {
        return this.details ? this.details.popularityLevel : 0;
    }

    @computed
    get videoUrl(): string | null {
        return this.details?.videoUrl || null;
    }

    @computed
    get rate(): number | null {
        return this.details?.rate || null;
    }

    @action
    setSsrInitialStateStore(store: InitialStateStore): void {
        this.initialStateStore = store;
        this.details = this.getInitialHotelInfo();
    }

    private getInitialHotelInfo(): HotelDetails | null {
        const countryDetails = this.initialStateStore?.countryDetails;
        const resortDetails = this.initialStateStore?.resortDetails;
        const hotelDetails = this.initialStateStore?.hotelDetails;
        const hotelPopularity = this.initialStateStore?.hotelPopularity;
        const beachType = hotelDetails?.beachTypes[0] || null;
        const beachSandType = hotelDetails?.beachSandTypes[0] || null;

        return {
            countryId: !!countryDetails ? countryDetails.id : null,
            countryName: !!countryDetails ? countryDetails.name : null,
            countryAlias: !!countryDetails ? countryDetails.alias : null,
            resortId: !!resortDetails ? resortDetails.id : null,
            resortName: !!resortDetails ? resortDetails.name : null,
            resortAlias: !!resortDetails ? resortDetails.alias : null,
            hotelId: hotelDetails?.id || null,
            hotelImagesCount: hotelDetails?.photosCount || null,
            hotelName: hotelDetails?.name || null,
            hotelOriginalName: hotelDetails?.originalName || null,
            hotelDescription: hotelDetails?.description || null,
            hotelEmail: hotelDetails?.email || null,
            hotelPhone: hotelDetails?.phone || null,
            longitude: hotelDetails?.longitude || null,
            latitude: hotelDetails?.latitude || null,
            numRooms: hotelDetails?.roomsCount || null,
            reconstructionYear: hotelDetails?.reconstructionYear || null,
            categoryName: hotelDetails?.category.name || null,
            viewsTodayCount: hotelDetails?.viewsTodayCount || null,
            tripAdvisorRate: hotelDetails?.tripAdvisorRate || null,
            originalPreviousName: hotelDetails?.originalPreviousName || null,
            beachType: !!beachType ? beachType.name : null,
            beachSandType: !!beachSandType ? beachSandType.name : null,
            distanceToAirport: hotelDetails?.distanceToAirport || null,
            checkInTime: hotelDetails?.checkInTime || null,
            checkOutTime: hotelDetails?.checkOutTime || null,
            distanceToBeach: hotelDetails?.distanceToBeach || null,
            howLongMinutesToCenterCity: getHowLongMinutesToCenterCity(
                hotelDetails?.distanceToCityCenter
            ),
            isCenterCity: hotelDetails?.isCenterCity || false,
            popularityLevel: hotelPopularity?.level || 0,
            videoUrl: hotelDetails?.videoUrl || null,
            rooms: hotelDetails?.rooms || [],
            rate: hotelDetails?.rate || null,
        };
    }

    private getFacilityWithGroupId(
        group: FacilityGroup
    ): FacilityWithGroupId | null {
        if (group.id === HotelBasicFacilityGroups.BeachLine) {
            return {
                groupId: group.id,
                ...group.facilities[0]
            } as FacilityWithGroupId;
        }

        const possibleFacilitiesIds = group.facilities.map((f) => f.id);
        const appropriateFacilities = intersectionWith(
            mappedBasicHotelFacilitiesIds[group.id],
            possibleFacilitiesIds
        );

        return appropriateFacilities.length
            ? ({
                  groupId: group.id,
                  ...group.facilities.find(
                      (f) => f.id === appropriateFacilities[0]
                  ),
              } as FacilityWithGroupId)
            : null;
    }
}
